import { DragOverlay } from '@dnd-kit/core';
import { SortableContext } from '@dnd-kit/sortable';
import type {
    DeepPartial,
    MoneyMapRuleGroup as MoneyMapRuleGroupType,
    MoneyMapRuleWithAccountDetails,
    MoneyMapWithRulesAndAccounts,
    RuleOrGroup,
} from '@fruitful/types';
import { MoneyMapRuleGoalType, MoneyMapRuleType } from '@fruitful/types';
import { Fragment, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { BodyMediumRegular, Trash2Icon, XStack, YStack } from 'smoothie';

import { GoalPanelPreset } from '../../utils/buildGoalPanelPresetGroupDto';
import { DropLine } from './DropLine';
import { GoalGroupSection } from './GoalGroupSection';
import {
    DELETE_DARK_RED,
    DELETE_LIGHT_BG,
    DELETE_RED,
    DELETE_SHADOW,
    goalDragRingShadow,
    staticSortingStrategy,
} from './goalsWaterfallConstants';
import type { GoalSelectionState } from './goalsWaterfallTypes';
import { GroupGoalRow } from './GroupGoalRow';
import { SingleGoalGroupSection } from './SingleGoalGroupSection';
import { SortableTopLevelItem } from './SortableTopLevelItem';
import type { GoalsWaterfallDnDControls } from './useGoalsWaterfallDnD';

interface PreviewRuleData {
    id: string;
    description: string;
    percentage: number;
    type: typeof MoneyMapRuleType.GOAL;
    goalType: MoneyMapRuleGoalType;
}

interface PreviewData {
    groupName: string;
    rules: PreviewRuleData[];
}

const CONTRIBUTE = MoneyMapRuleGoalType.CONTRIBUTE_INDEFINITELY;
const BUILD_UP = MoneyMapRuleGoalType.BUILD_UP_TARGET_BALANCE;
const DEBT = MoneyMapRuleGoalType.DEBT_PAY_DOWN;

const PRESET_DROP_PREVIEW: Record<GoalPanelPreset, PreviewData> = {
    [GoalPanelPreset.NEW_GOAL]: {
        groupName: 'New goal',
        rules: [
            {
                id: 'p0',
                description: 'New goal',
                percentage: 1,
                type: MoneyMapRuleType.GOAL,
                goalType: CONTRIBUTE,
            },
        ],
    },
    [GoalPanelPreset.ONE_MONTH_EMERGENCY_FUND]: {
        groupName: '1 Month Emergency Fund',
        rules: [
            {
                id: 'p0',
                description: '1 Month Emergency Fund',
                percentage: 1,
                type: MoneyMapRuleType.GOAL,
                goalType: BUILD_UP,
            },
        ],
    },
    [GoalPanelPreset.FULL_EMERGENCY_FUND]: {
        groupName: 'Full Emergency Fund',
        rules: [
            {
                id: 'p0',
                description: 'Full Emergency Fund',
                percentage: 1,
                type: MoneyMapRuleType.GOAL,
                goalType: BUILD_UP,
            },
        ],
    },
    [GoalPanelPreset.FINANCIAL_HEALTH]: {
        groupName: 'Financial Health',
        rules: [
            {
                id: 'p0',
                description: 'Debt pay down',
                percentage: 0.8,
                type: MoneyMapRuleType.GOAL,
                goalType: DEBT,
            },
            {
                id: 'p1',
                description: 'Full Emergency Fund',
                percentage: 0.15,
                type: MoneyMapRuleType.GOAL,
                goalType: BUILD_UP,
            },
            {
                id: 'p2',
                description: 'Retirement',
                percentage: 0.05,
                type: MoneyMapRuleType.GOAL,
                goalType: CONTRIBUTE,
            },
        ],
    },
    [GoalPanelPreset.INVESTMENT_FUND]: {
        groupName: 'Investment Fund',
        rules: [
            {
                id: 'p0',
                description: 'Investment fund',
                percentage: 0.05,
                type: MoneyMapRuleType.GOAL,
                goalType: CONTRIBUTE,
            },
        ],
    },
    [GoalPanelPreset.BACKDOOR_ROTH]: {
        groupName: 'Backdoor Roth',
        rules: [
            {
                id: 'p0',
                description: 'Backdoor Roth',
                percentage: 0.05,
                type: MoneyMapRuleType.GOAL,
                goalType: CONTRIBUTE,
            },
        ],
    },
    [GoalPanelPreset.BACKDOOR_ROTH_PARTNER]: {
        groupName: 'Backdoor Roth',
        rules: [
            {
                id: 'p0',
                description: 'Backdoor Roth',
                percentage: 0.05,
                type: MoneyMapRuleType.GOAL,
                goalType: CONTRIBUTE,
            },
        ],
    },
    [GoalPanelPreset.PAY_OFF_DEBT]: {
        groupName: 'Pay off debt',
        rules: [
            {
                id: 'p0',
                description: 'Pay off debt',
                percentage: 1,
                type: MoneyMapRuleType.GOAL,
                goalType: DEBT,
            },
        ],
    },
    [GoalPanelPreset.DOWN_PAYMENT]: {
        groupName: 'Down payment',
        rules: [
            {
                id: 'p0',
                description: 'Down payment',
                percentage: 1,
                type: MoneyMapRuleType.GOAL,
                goalType: BUILD_UP,
            },
        ],
    },
    [GoalPanelPreset.EDUCATION_FUND]: {
        groupName: 'Education fund',
        rules: [
            {
                id: 'p0',
                description: 'Education fund',
                percentage: 1,
                type: MoneyMapRuleType.GOAL,
                goalType: CONTRIBUTE,
            },
        ],
    },
    [GoalPanelPreset.BUY_A_CAR]: {
        groupName: 'Buy a car',
        rules: [
            {
                id: 'p0',
                description: 'Buy a car',
                percentage: 1,
                type: MoneyMapRuleType.GOAL,
                goalType: BUILD_UP,
            },
        ],
    },
    [GoalPanelPreset.TRAVEL_FUND]: {
        groupName: 'Travel fund',
        rules: [
            {
                id: 'p0',
                description: 'Travel fund',
                percentage: 1,
                type: MoneyMapRuleType.GOAL,
                goalType: CONTRIBUTE,
            },
        ],
    },
    [GoalPanelPreset.GIFTS_FUND]: {
        groupName: 'Gifts fund',
        rules: [
            {
                id: 'p0',
                description: 'Gifts fund',
                percentage: 1,
                type: MoneyMapRuleType.GOAL,
                goalType: CONTRIBUTE,
            },
        ],
    },
};

const PresetDropPreviewCard = ({
    preset,
    insertIndex,
    moneyMap,
}: {
    preset: GoalPanelPreset;
    insertIndex: number;
    moneyMap: DeepPartial<MoneyMapWithRulesAndAccounts>;
}) => {
    const previewData = PRESET_DROP_PREVIEW[preset];
    const numNonGoalRules = (moneyMap.moneyMapRules ?? []).filter(
        (r) =>
            r.type === MoneyMapRuleType.BILLS ||
            r.type === MoneyMapRuleType.SPEND,
    ).length;
    const insertPriority = insertIndex + 1 + numNonGoalRules;
    const fakeGroup: DeepPartial<MoneyMapRuleGroupType> = {
        id: 'preview-group',
        name: previewData.groupName,
        priority: insertPriority,
    };

    return (
        <YStack opacity={0.5} pointerEvents="none" marginBottom={12}>
            {previewData.rules.length === 1 && previewData.rules[0] != null ? (
                <SingleGoalGroupSection
                    group={fakeGroup}
                    rule={previewData.rules[0]}
                    moneyMap={moneyMap}
                    isDragOverlay
                />
            ) : (
                <GoalGroupSection
                    group={fakeGroup}
                    groupRules={previewData.rules}
                    moneyMap={moneyMap}
                    isDragOverlay
                    dropIndicator={null}
                />
            )}
        </YStack>
    );
};

interface GoalsWaterfallListProps {
    moneyMap: DeepPartial<MoneyMapWithRulesAndAccounts>;
    goalRules: Array<DeepPartial<MoneyMapRuleWithAccountDetails>>;
    goalItems: RuleOrGroup[];
    topLevelIds: string[];
    getGroupRules: (
        groupId: string | undefined,
    ) => Array<DeepPartial<MoneyMapRuleWithAccountDetails>>;
    previewInsertIndex?: number | null;
    previewPreset?: GoalPanelPreset | null;
    selectedGoal?: GoalSelectionState;
    onSelectGoal?: (selection: GoalSelectionState) => void;
    /** Provided by the page-level `DndContext` (must not nest another context). */
    goalsWaterfallDnd: GoalsWaterfallDnDControls;
}

export const GoalsWaterfallList = ({
    moneyMap,
    goalRules,
    goalItems,
    topLevelIds,
    getGroupRules,
    previewInsertIndex,
    previewPreset,
    selectedGoal,
    onSelectGoal,
    goalsWaterfallDnd,
}: GoalsWaterfallListProps) => {
    const {
        activeDrag,
        dropIndicator,
        activeDragGoalItem,
        activeDragGroupGoal,
        overlayWrapperStyle,
        isPendingDelete,
    } = goalsWaterfallDnd;

    const overlayDragHandleProps = useMemo(
        () => ({ setActivatorNodeRef: undefined }),
        [],
    );

    const deleteTargetDescription = (() => {
        if (activeDragGroupGoal != null) return activeDragGroupGoal.description;
        if (activeDragGoalItem == null) return undefined;
        if (activeDragGoalItem.entityType === 'rule')
            return goalRules.find((r) => r.id === activeDragGoalItem.id)
                ?.description;
        // Single-goal group: show the inner rule's description.
        return getGroupRules(activeDragGoalItem.id)[0]?.description;
    })();

    return (
        <>
            <SortableContext
                items={topLevelIds}
                strategy={staticSortingStrategy}
            >
                {goalItems.map((item, index) => {
                    const suppressTopLevelDropLines =
                        activeDrag?.type === 'preset';
                    const showLineBefore =
                        !suppressTopLevelDropLines &&
                        dropIndicator?.kind === 'line-before' &&
                        dropIndicator.targetId === item.id;
                    const showLineAfter =
                        !suppressTopLevelDropLines &&
                        dropIndicator?.kind === 'line-after' &&
                        dropIndicator.targetId === item.id;
                    const showGroupOutline =
                        (dropIndicator?.kind === 'group-outline' &&
                            dropIndicator.targetId === item.id) ||
                        dropIndicator?.groupId === item.id;

                    return (
                        <Fragment key={item.id}>
                            {previewInsertIndex === index &&
                                previewPreset != null && (
                                    <PresetDropPreviewCard
                                        preset={previewPreset}
                                        insertIndex={index}
                                        moneyMap={moneyMap}
                                    />
                                )}
                            {showLineBefore && <DropLine />}
                            <SortableTopLevelItem
                                id={item.id ?? ''}
                                ghostHeight={activeDrag?.height ?? 64}
                                data={{
                                    type:
                                        item.entityType === 'group'
                                            ? 'top-group'
                                            : 'top-goal',
                                }}
                            >
                                {({
                                    setActivatorNodeRef,
                                    listeners,
                                    attributes,
                                }) => {
                                    if (item.entityType === 'group') {
                                        const groupRules = getGroupRules(
                                            item.id,
                                        );
                                        const singleRule = groupRules[0];
                                        if (
                                            groupRules.length === 1 &&
                                            singleRule != null
                                        ) {
                                            const isSingleSelected =
                                                selectedGoal?.type === 'goal' &&
                                                selectedGoal.goalId ===
                                                    singleRule.id;
                                            return (
                                                <SingleGoalGroupSection
                                                    group={item}
                                                    rule={singleRule}
                                                    moneyMap={moneyMap}
                                                    showOutline={
                                                        showGroupOutline
                                                    }
                                                    dragHandleProps={{
                                                        setActivatorNodeRef,
                                                        listeners,
                                                        attributes,
                                                    }}
                                                    isSelected={
                                                        isSingleSelected
                                                    }
                                                    onPress={
                                                        onSelectGoal != null &&
                                                        singleRule.id != null &&
                                                        item.id != null
                                                            ? () => {
                                                                  const goalId =
                                                                      singleRule.id;
                                                                  const groupId =
                                                                      item.id;
                                                                  if (
                                                                      goalId ==
                                                                          null ||
                                                                      groupId ==
                                                                          null
                                                                  ) {
                                                                      return;
                                                                  }
                                                                  onSelectGoal({
                                                                      type: 'goal',
                                                                      goalId,
                                                                      groupId,
                                                                  });
                                                              }
                                                            : undefined
                                                    }
                                                />
                                            );
                                        }
                                        const isGroupSelected =
                                            selectedGoal?.type === 'group' &&
                                            selectedGoal.groupId === item.id;
                                        return (
                                            <GoalGroupSection
                                                group={item}
                                                groupRules={groupRules}
                                                moneyMap={moneyMap}
                                                showOutline={
                                                    showGroupOutline ||
                                                    isGroupSelected
                                                }
                                                dropIndicator={dropIndicator}
                                                dragHandleProps={{
                                                    setActivatorNodeRef,
                                                    listeners,
                                                    attributes,
                                                }}
                                                selectedGoalId={
                                                    selectedGoal?.type ===
                                                    'goal'
                                                        ? selectedGoal.goalId
                                                        : null
                                                }
                                                onPressGroupHeader={
                                                    onSelectGoal != null &&
                                                    item.id != null
                                                        ? () => {
                                                              const groupId =
                                                                  item.id;
                                                              if (
                                                                  groupId ==
                                                                  null
                                                              ) {
                                                                  return;
                                                              }
                                                              onSelectGoal({
                                                                  type: 'group',
                                                                  groupId,
                                                              });
                                                          }
                                                        : undefined
                                                }
                                                onPressGoal={
                                                    onSelectGoal != null
                                                        ? (goalId) => {
                                                              onSelectGoal({
                                                                  type: 'goal',
                                                                  goalId,
                                                                  groupId:
                                                                      item.id,
                                                              });
                                                          }
                                                        : undefined
                                                }
                                                onPressGoalPercentage={
                                                    onSelectGoal != null &&
                                                    item.id != null
                                                        ? () => {
                                                              const groupId =
                                                                  item.id;
                                                              if (
                                                                  groupId ==
                                                                  null
                                                              ) {
                                                                  return;
                                                              }
                                                              onSelectGoal({
                                                                  type: 'group',
                                                                  groupId,
                                                              });
                                                          }
                                                        : undefined
                                                }
                                            />
                                        );
                                    }
                                    return null;
                                }}
                            </SortableTopLevelItem>
                            {showLineAfter && <DropLine />}
                        </Fragment>
                    );
                })}
                {previewInsertIndex === goalItems.length &&
                    previewPreset != null && (
                        <PresetDropPreviewCard
                            preset={previewPreset}
                            insertIndex={goalItems.length}
                            moneyMap={moneyMap}
                        />
                    )}
            </SortableContext>

            {typeof document !== 'undefined' &&
                createPortal(
                    <DragOverlay dropAnimation={null}>
                        {isPendingDelete &&
                        (activeDragGoalItem != null ||
                            activeDragGroupGoal != null) ? (
                            <XStack
                                width={activeDrag?.width}
                                height={activeDrag?.height}
                                borderRadius={
                                    activeDrag?.type === 'group-goal' ? 12 : 16
                                }
                                backgroundColor={DELETE_LIGHT_BG}
                                overflow="hidden"
                                padding={activeDragGroupGoal != null ? 12 : 16}
                                gap={12}
                                alignItems="center"
                                opacity={1}
                                cursor="grabbing"
                                style={{
                                    boxSizing: 'border-box',
                                    flexShrink: 0,
                                    minWidth: activeDrag?.width,
                                    minHeight: activeDrag?.height,
                                    boxShadow: `${goalDragRingShadow(DELETE_RED)}, 0 8px 24px ${DELETE_SHADOW}, 0 4px 12px rgba(0,0,0,0.08)`,
                                }}
                            >
                                <XStack
                                    flexShrink={0}
                                    style={{
                                        color: DELETE_DARK_RED,
                                        strokeWidth: '1.5',
                                    }}
                                >
                                    <Trash2Icon size={20} />
                                </XStack>
                                <YStack flex={1} minWidth={0}>
                                    <BodyMediumRegular numberOfLines={1}>
                                        {deleteTargetDescription}
                                    </BodyMediumRegular>
                                </YStack>
                                <BodyMediumRegular
                                    color={DELETE_DARK_RED}
                                    flexShrink={0}
                                    style={{ whiteSpace: 'nowrap' }}
                                >
                                    Delete goal
                                </BodyMediumRegular>
                            </XStack>
                        ) : (
                            <>
                                {activeDragGoalItem?.entityType === 'group' && (
                                    <YStack style={overlayWrapperStyle}>
                                        {(() => {
                                            const overlayGroupRules =
                                                getGroupRules(
                                                    activeDragGoalItem.id,
                                                );
                                            const overlaySingleRule =
                                                overlayGroupRules[0];
                                            if (
                                                overlayGroupRules.length ===
                                                    1 &&
                                                overlaySingleRule != null
                                            ) {
                                                return (
                                                    <SingleGoalGroupSection
                                                        group={
                                                            activeDragGoalItem
                                                        }
                                                        rule={overlaySingleRule}
                                                        moneyMap={moneyMap}
                                                        isDragOverlay
                                                        dragHandleProps={
                                                            overlayDragHandleProps
                                                        }
                                                    />
                                                );
                                            }
                                            return (
                                                <GoalGroupSection
                                                    group={activeDragGoalItem}
                                                    groupRules={
                                                        overlayGroupRules
                                                    }
                                                    moneyMap={moneyMap}
                                                    isDragOverlay
                                                    dropIndicator={null}
                                                    dragHandleProps={
                                                        overlayDragHandleProps
                                                    }
                                                />
                                            );
                                        })()}
                                    </YStack>
                                )}
                                {activeDragGroupGoal != null && (
                                    <YStack style={overlayWrapperStyle}>
                                        <GroupGoalRow
                                            rule={activeDragGroupGoal}
                                            isDragOverlay
                                            dragHandleProps={
                                                overlayDragHandleProps
                                            }
                                        />
                                    </YStack>
                                )}
                            </>
                        )}
                    </DragOverlay>,
                    document.body,
                )}
        </>
    );
};
